import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, Lock, User, ArrowRight } from "lucide-react";
import { HomeLensLogo } from "@/components/HomeLensLogo";
import { toast } from "sonner";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register, user } = useAuth();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("buyer");
  const [loading, setLoading] = useState(false);

  // Redirect if already logged in
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);
    
    try {
      const data = await register(email, password, fullName, role);
      if (data?.session) {
        toast.success("Account created! Welcome to HomeLens.");
        navigate("/dashboard");
      } else {
        toast.success("Account created! Please check your email to confirm.");
        navigate("/login");
      }
    } catch (error) {
      toast.error(error.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const roles = [
    { value: "agent", label: "Agent", description: "Create and manage transactions" },
    { value: "buyer", label: "Buyer", description: "View transactions you're part of" },
    { value: "seller", label: "Seller", description: "View transactions you're part of" },
  ];

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 gradient-hero">
      <div className="w-full max-w-sm animate-slideUp">
        {/* Logo */}
        <div className="flex items-center justify-center mb-8">
          <HomeLensLogo size="lg" />
        </div>

        <Card className="border-border/60 shadow-soft">
          <CardHeader className="text-center pb-2 pt-6">
            <CardTitle className="font-heading text-xl font-bold tracking-tight">Create Account</CardTitle>
            <CardDescription className="text-sm">Join HomeLens to manage real estate transactions</CardDescription>
          </CardHeader>
          <CardContent className="pt-4 pb-6">
            {/* Registration Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-sm font-medium">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Smith"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="pl-10 h-10 rounded-md"
                    required
                    data-testid="name-input"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-sm font-medium">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-10 rounded-md"
                    required
                    data-testid="register-email-input"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 h-10 rounded-md"
                    minLength={6}
                    required
                    data-testid="register-password-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground">At least 6 characters</p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="role" className="text-sm font-medium">I am a...</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger className="h-10 rounded-md" data-testid="role-select">
                    <SelectValue placeholder="Select your role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        <span className="font-medium">{r.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {roles.find(r => r.value === role)?.description}
                </p>
              </div>

              <Button 
                type="submit" 
                className="w-full h-10 rounded-md btn-press text-sm font-medium"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground"></div>
                ) : (
                  <>
                    Create Account
                    <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                  </>
                )}
              </Button>
            </form>

            <p className="text-center text-sm text-muted-foreground mt-5">
              Already have an account?{" "}
              <Link to="/login" className="text-primary hover:underline font-medium" data-testid="login-link">
                Sign in
              </Link>
            </p>
          </CardContent>
        </Card>

        {/* Back to home */}
        <p className="text-center text-sm text-muted-foreground mt-6">
          <Link to="/" className="hover:text-foreground transition-colors">
            &larr; Back to Home
          </Link>
        </p>
      </div>
    </div>
  );
}
